import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { BehaviorSubject, fromEvent, Observable } from 'rxjs'
import { ComponentVariant } from '../types'

@customElement('e-input')
export class EInput extends LitElement {
    private _value$ = new BehaviorSubject('')
    private _inputElement: HTMLInputElement | null = null
    public input$!: Observable<Event>

    @property({ type: String })
    variant: ComponentVariant = 'accent'

    @property({ type: String })
    size: 'small' | 'medium' | 'large' = 'medium'

    @property({ type: String })
    placeholder = ''

    @property({ type: Boolean })
    fullWidth = false

    @property({ type: Boolean })
    grow = false

    @property({ type: String })
    get value(): string {
        return this._value$.value
    }
    set value(v: string) {
        this._value$.next(v)
    }

    static styles = css`
        /* Use inline-flex so that this component can be placed alongside sibling components */
        :host {
            display: inline-flex;
            box-sizing: border-box;
        }

        :host([fullWidth]) {
            width: 100%;
        }

        :host([grow]) {
            flex-grow: 1;
            width: auto;
        }

        /* The inner input takes available space using flex */
        input {
            font-family: var(--base-font-family, sans-serif);
            font-size: var(--font-size-button);
            color: var(--text-primary);
            background-color: var(--bg-secondary);
            border: 2px solid var(--border-color);
            padding: var(--s0) var(--s1);
            border-radius: var(--radius-medium);
            transition: var(--transition-fast);
            box-sizing: border-box;
            flex: 1;
            min-width: 0;
            box-sizing: border-box;
            height: var(--control-height);
        }

        /* Variant styles based on our common type */
        input.variant-accent {
            border-color: var(--primary-color);
        }
        input.variant-playful {
            border-color: var(--interactive-color);
        }
        input.variant-gradient {
            border: 2px solid;
            border-image-slice: 1;
            border-image-source: linear-gradient(
                to right,
                var(--gradient-start),
                var(--gradient-end)
            );
        }

        /* Size modifiers using global spacing and typography variables */
        input.size-small {
            font-size: calc(var(--font-size-button) * 0.85);
            padding: calc(var(--s0) * 0.75) calc(var(--s1) * 0.75);
        }
        input.size-medium {
            font-size: var(--font-size-button);
            padding: var(--s0) var(--s1);
        }
        input.size-large {
            font-size: calc(var(--font-size-button) * 1.15);
            padding: calc(var(--s0) * 1.25) calc(var(--s1) * 1.25);
        }

        /* Focus styles with a consistent outline shadow */
        input:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--primary-color);
        }
    `

    firstUpdated() {
        this._inputElement = this.shadowRoot?.querySelector('input') as HTMLInputElement
        if (this._inputElement) {
            this.input$ = fromEvent(this._inputElement, 'keydown')
        }
    }

    #handleInput(e: Event) {
        this.value = (e.target as HTMLInputElement).value
        this.dispatchEvent(
            new CustomEvent('value-changed', {
                detail: { value: this.value },
            }),
        )
    }

    #handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            this.dispatchEvent(
                new CustomEvent('enter-pressed', {
                    bubbles: true,
                    composed: true,
                }),
            )
        }
    }

    render() {
        /* Compute the variant class using the common variant type */
        const inputClassList = [`size-${this.size}`, `variant-${this.variant}`].join(' ')
        return html`
            <input
                class="${inputClassList}"
                type="text"
                placeholder="${this.placeholder}"
                .value="${this.value}"
                @input="${this.#handleInput}"
                @keydown="${this.#handleKeyDown}"
            />
        `
    }
}
